
export type AuthorizedFetch = (
  url: string,
  init?: RequestInit,
) => Promise<Response>;

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * Per-call knobs. `signal` lets a caller cancel an in-flight request — used for
 * client-side timeouts and for abandoning work when a panel unmounts. Optional
 * throughout, so existing call sites are unaffected.
 */
export interface HttpRequestOptions {
  signal?: AbortSignal;
}

export interface HttpClient {
  get<T>(path: string, options?: HttpRequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<T>;
  del<T>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<T>;
  /** Raw authorized fetch — for binary/streaming/absolute-URL cases. */
  fetch: AuthorizedFetch;
}

/**
 * Thrown when a Deckorate request resolves with a non-2xx status — carries the
 * status code so callers can distinguish "the bearer is unrecoverably expired"
 * (401, surviving `fetchWithRefresh`'s own silent-refresh retry) from any other
 * failure, instead of pattern-matching the message text.
 */
export class HttpStatusError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "HttpStatusError";
  }
}

function buildUrl(base: string, path: string): string {
  if (!base) {
    throw new Error("Deckorate API base URL (config.deckorateApiBaseUrl) is not configured.");
  }
  return base.replace(/\/$/, "") + path;
}

export function createHttpClient(
  authorizedFetch: AuthorizedFetch,
  baseUrl: string,
): HttpClient {
  async function request<T>(
    method: HttpMethod,
    path: string,
    payload?: unknown,
    options?: HttpRequestOptions,
  ): Promise<T> {
    const headers: Record<string, string> = {};
    let body: BodyInit | undefined;
    if (payload !== undefined) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(payload);
    }
    const res = await authorizedFetch(buildUrl(baseUrl, path), {
      method,
      headers,
      body,
      ...(options?.signal ? { signal: options.signal } : {}),
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => "");
      throw new HttpStatusError(res.status, `Deckorate ${method} ${path}: ${res.status} ${errText}`);
    }
    const text = await res.text();
    return (text ? JSON.parse(text) : null) as T;
  }

  return {
    get: (path, options) => request("GET", path, undefined, options),
    post: (path, body, options) => request("POST", path, body, options),
    put: (path, body, options) => request("PUT", path, body, options),
    patch: (path, body, options) => request("PATCH", path, body, options),
    del: (path, body, options) => request("DELETE", path, body, options),
    fetch: authorizedFetch,
  };
}
